import React, { useState, useEffect } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { useNavigate } from 'react-router-dom';
import { config } from '../../config/environment';
import { authService } from '../../services/authService';
import { 
  getFormattedStateOptions, 
  getFormattedCityOptions 
} from '../../utils/addressUtils';
import './ProviderProfile.css';

// API Response Types
interface PersonalInfo {
  firstName: string;
  lastName: string;
  phone: number;
  dob: string;
}

interface Address {
  id: number | null;
  addressUuid: string | null;
  addressType: string;
  label?: string;
  line1: string;
  line2: string;
  cityId: number;
  stateId: number;
  cityName?: string;    // Response includes display names
  stateName?: string;   // Response includes display names  
  city?: string;        // Response includes display names for backward compatibility
  state?: string;       // Response includes display names for backward compatibility
  postalCode: string;
  isDefault?: boolean;
  default?: boolean;    // Response uses 'default' instead of 'isDefault'
  createdAt: string | null;
}

interface ProfessionalInfo {
  experienceYears: number;
  profileStatus: string;
  gstNo: string;
  bio: string;
  availabilityType?: string;
}

interface DocumentInfo {
  originalName: string;
  contentType: string;
  size: number;
  downloadUrl: string;
  exists: boolean;
}

interface BankingDocuments {
  accountHolderName: string;
  accountNumber: string;
  ifscCode: string;
  bankName: string;
  panCard: DocumentInfo;
  aadharCard: DocumentInfo;
  miscDoc: DocumentInfo;
}

interface DocumentUploadResponse {
  documentType: string;
  originalFileName: string;
  contentType: string;
  size: number;
  downloadUrl: string;
  uploadedAt: string;
  message: string;
}

interface ProviderProfileResponse {
  personalInfo: PersonalInfo;
  address: Address;
  professionalInfo: ProfessionalInfo;
  bankingDocuments: BankingDocuments;
}

interface ProviderProfileData {
  // Personal Information
  firstName: string;
  lastName: string;
  email: string;
  phone: string;
  dateOfBirth: string;
  
  // Professional Information
  businessName: string;
  experience: string;
  specializations: string[];
  languages: string[];
  certifications: string[];
  gstNumber: string;
  currentStatus: string;
  
  // Address Information
  addressLine1: string;
  addressLine2: string;
  city: string;      // Display name for form
  state: string;     // Display name for form
  pincode: string;
  serviceAreas: string[];
  
  // Master data selections
  selectedStateId: number;
  selectedCityId: number;
  
  // Business Details
  bio: string;
  priceRange: string;
  availability: string;
  bankDetails: {
    accountHolderName: string;
    accountNumber: string;
    ifscCode: string;
    bankName: string;
  };
  
  // Documents
  profileImage: string;
  documents: {
    aadharCard: string;
    panCard: string;
    businessLicense: string;
  };
}

const ProviderProfile: React.FC = () => {
  const { user, isProviderUnderReview, isProvider } = useAuth();
  const navigate = useNavigate();

  const [profileData, setProfileData] = useState<ProviderProfileData>({
    firstName: '',
    lastName: '',
    email: user?.email || '',
    phone: '',
    dateOfBirth: '',
    businessName: '',
    experience: '',
    specializations: [],
    languages: [],
    certifications: [],
    gstNumber: '',
    currentStatus: '',
    addressLine1: '',
    addressLine2: '',
    city: '',
    state: '',
    pincode: '',
    serviceAreas: [],
    selectedStateId: 0,
    selectedCityId: 0,
    bio: '',
    priceRange: '',
    availability: '',
    bankDetails: {
      accountHolderName: '',
      accountNumber: '',
      ifscCode: '',
      bankName: ''
    },
    profileImage: '',
    documents: {
      aadharCard: '',
      panCard: '',
      businessLicense: ''
    }
  });

  const [activeTab, setActiveTab] = useState('personal');
  const [isSaving, setIsSaving] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [successMessage, setSuccessMessage] = useState('');
  const [isNewProvider, setIsNewProvider] = useState(false);
  const [isLegitimateNewProvider, setIsLegitimateNewProvider] = useState(false);
  const [showLoadProfileOption, setShowLoadProfileOption] = useState(false);
  
  // Validation states
  const [validationErrors, setValidationErrors] = useState<{[key: string]: string}>({});
  const [showValidation, setShowValidation] = useState(false);
  
  // Master data states
  const [stateOptions, setStateOptions] = useState<Array<{value: number; label: string; code: string}>>([]);
  const [cityOptions, setCityOptions] = useState<Array<{value: number; label: string; stateId: number}>>([]);
  const [isLoadingStates, setIsLoadingStates] = useState(false);
  const [isLoadingCities, setIsLoadingCities] = useState(false);
  
  // Document upload states
  const [uploadingPanCard, setUploadingPanCard] = useState(false);
  const [uploadingAadharCard, setUploadingAadharCard] = useState(false);
  const [uploadingMiscDoc, setUploadingMiscDoc] = useState(false);

  // Master data loading functions
  const loadStates = async () => {
    try {
      setIsLoadingStates(true);
      const states = await getFormattedStateOptions();
      setStateOptions(states);
    } catch (error) {
      console.error('Error loading states:', error);
    } finally {
      setIsLoadingStates(false);
    }
  };

  const loadCities = async (stateId: number) => {
    if (stateId <= 0) {
      setCityOptions([]);
      return;
    }
    
    try {
      setIsLoadingCities(true);
      const cities = await getFormattedCityOptions(stateId);
      setCityOptions(cities);
    } catch (error) {
      console.error('Error loading cities:', error);
      setCityOptions([]);
    } finally {
      setIsLoadingCities(false);
    }
  };

  // Load states on component mount
  useEffect(() => {
    loadStates();
  }, []);

  // Load cities when state changes
  useEffect(() => {
    if (profileData.selectedStateId > 0) {
      loadCities(profileData.selectedStateId);
    } else {
      setCityOptions([]);
    }
  }, [profileData.selectedStateId]);

  // Fetch profile data on component mount
  useEffect(() => {
    fetchProfileData();
  }, [user?.userUuid]);

  // Redirect active providers to dashboard unless they explicitly want to edit profile
  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const editMode = urlParams.get('edit') === 'true';
    
    // If provider is active and not in edit mode, redirect to dashboard
    // Only redirect after loading is complete to avoid race conditions
    if (user && !loading && !isProviderUnderReview() && !editMode) {
      console.log('🔄 Redirecting active provider to dashboard');
      navigate('/dashboard/provider', { replace: true });
    }
  }, [user, isProviderUnderReview, loading, navigate]);

  const fetchProfileData = async () => {
    if (!user?.userUuid) return;

    try {
      setLoading(true);
      const token = localStorage.getItem('access_token');
      if (!token) throw new Error('No access token found');

      console.log('📡 Calling profile API to load existing provider data');
      // Use authService.getApiInstance() to leverage token refresh interceptor
      const response = await authService.getApiInstance().get(
        `/api/provider/${user.userUuid}/profile`
      );

      if (response.status === 200) {
        // Provider has existing profile data
        console.log('✅ Found existing provider profile');
        const data: ProviderProfileResponse = response.data;
        mapApiDataToState(data);
        
        // Set appropriate states based on provider review status
        if (isProviderUnderReview()) {
          console.log('👋 Provider under review with existing data - showing welcome message');
          setIsNewProvider(true);
          setIsLegitimateNewProvider(true);
        } else {
          setIsNewProvider(false);
          setIsLegitimateNewProvider(false);
        }
      }

    } catch (err: any) {
      // If API call fails, determine if it's a network error or new provider case
      console.log('Profile API failed, analyzing error type:', err);
      
      // Check if it's a 404 error (provider not found) - treat as legitimate new provider
      if (err.response?.status === 404) {
        console.log('📝 New provider detected (404) - showing welcome message and using basic user info');
        setIsNewProvider(true);
        setIsLegitimateNewProvider(true);
        setShowLoadProfileOption(true); // Show option to load existing profile
        setProfileData(prev => ({
          ...prev,
          firstName: user?.firstName || '',
          lastName: user?.lastName || '',
          email: user?.email || ''
        }));
        // Don't set error for legitimate new providers
      } else {
        console.log('📝 Real API error - not showing welcome message');
        const errorMessage = err.response?.data?.message || err.message || 'Unknown error occurred';
        setIsNewProvider(true);
        setIsLegitimateNewProvider(false); // This is an error case, not a legitimate new provider
        setShowLoadProfileOption(true); // Show option to retry loading profile
        setProfileData(prev => ({
          ...prev,
          firstName: user?.firstName || '',
          lastName: user?.lastName || '',
          email: user?.email || ''
        }));
        setError('Could not load complete profile data. Please fill in the required information.');
      }
    } finally {
      setLoading(false);
    }
  };

  const loadProfileDataExplicitly = async () => {
    if (!user?.userUuid) return;

    try {
      setLoading(true);
      setShowLoadProfileOption(false);

      console.log('📡 Explicitly calling profile API per user request');
      // Use authService.getApiInstance() to leverage token refresh interceptor
      const response = await authService.getApiInstance().get(
        `/api/provider/${user.userUuid}/profile`
      );

      if (response.status === 200) {
        console.log('✅ Found existing provider profile on explicit load');
        const data: ProviderProfileResponse = response.data;
        mapApiDataToState(data);
        setIsNewProvider(false);
        setIsLegitimateNewProvider(false);
      } 

    } catch (err: any) {
      console.log('Profile load failed:', err);
      if (err.response?.status === 404) {
        console.log('New provider confirmed (404) - keeping welcome message');
        // Keep the new provider state as is
      } else {
        setError('Could not load profile data. You can continue as a new provider.');
      }
    } finally {
      setLoading(false);
    }
  };

  const mapApiDataToState = (data: ProviderProfileResponse) => {
    // Map API response to component state
    setProfileData(prev => ({
      ...prev,
      // Personal Info
      firstName: data.personalInfo?.firstName || user?.firstName || '',
      lastName: data.personalInfo?.lastName || user?.lastName || '',
      phone: data.personalInfo?.phone?.toString() || '',
      dateOfBirth: data.personalInfo?.dob || '',
      
      // Address Info - handle new API structure with cityId/stateId
      addressLine1: data.address?.line1 || '',
      addressLine2: data.address?.line2 || '',
      city: data.address?.cityName || data.address?.city || '',
      state: data.address?.stateName || data.address?.state || '',
      pincode: data.address?.postalCode || '',
      selectedStateId: data.address?.stateId || 0,
      selectedCityId: data.address?.cityId || 0,
      
      // Professional Info
      experience: data.professionalInfo?.experienceYears ? mapExperienceYearsToOption(data.professionalInfo.experienceYears) : '',
      currentStatus: mapProfileStatusToCurrentStatus(data.professionalInfo?.profileStatus),
      gstNumber: data.professionalInfo?.gstNo || '',
      bio: data.professionalInfo?.bio || '',
      availability: mapAvailabilityTypeToAvailability(data.professionalInfo?.availabilityType || ''),
      
      // Banking Info
      bankDetails: {
        accountHolderName: data.bankingDocuments?.accountHolderName || '',
        accountNumber: data.bankingDocuments?.accountNumber || '',
        ifscCode: data.bankingDocuments?.ifscCode || '',
        bankName: data.bankingDocuments?.bankName || ''
      },
      
      // Documents
      documents: {
        aadharCard: (data.bankingDocuments?.aadharCard?.exists && data.bankingDocuments?.aadharCard?.downloadUrl) ? data.bankingDocuments.aadharCard.downloadUrl : '',
        panCard: (data.bankingDocuments?.panCard?.exists && data.bankingDocuments?.panCard?.downloadUrl) ? data.bankingDocuments.panCard.downloadUrl : '',
        businessLicense: (data.bankingDocuments?.miscDoc?.exists && data.bankingDocuments?.miscDoc?.downloadUrl) ? data.bankingDocuments.miscDoc.downloadUrl : ''
      }
    }));
  };

  const specializationOptions = [
    'Ganesh Puja', 'Rudrabhishek', 'Lakshmi Puja', 'Hanuman Chalisa',
    'Satyanarayan Puja', 'Graha Shanti', 'Vastu Puja', 'Marriage Ceremonies',
    'Housewarming Puja', 'Thread Ceremony', 'Naming Ceremony', 'Diwali Puja'
  ];

  const languageOptions = [
    'Hindi', 'English', 'Sanskrit', 'Marathi', 'Gujarati', 'Bengali',
    'Tamil', 'Telugu', 'Kannada', 'Malayalam', 'Punjabi', 'Odia'
  ];

  // Helper function to map experience years to dropdown options
  const mapExperienceYearsToOption = (years: number): string => {
    if (years >= 1 && years <= 2) return '1-2';
    if (years >= 3 && years <= 5) return '3-5';
    if (years >= 6 && years <= 10) return '6-10';
    if (years >= 11 && years <= 15) return '11-15';
    if (years > 15) return '15+';
    return '';
  };

  // Helper function to map API profileStatus to currentStatus dropdown
  const mapProfileStatusToCurrentStatus = (profileStatus: string): string => {
    // You can customize this mapping based on your business logic
    // For now, keeping it simple - you might want to adjust this
    return profileStatus?.toLowerCase() || '';
  };

  // Helper function to map API availabilityType to availability dropdown
  const mapAvailabilityTypeToAvailability = (availabilityType: string): string => {
    switch (availabilityType) {
      case 'WEEKDAYS': return 'WEEKDAYS';
      case 'WEEKENDS': return 'WEEKENDS';
      case 'ALL_DAYS': return 'ALL_DAYS';
      case 'FLEXIBLE': return 'FLEXIBLE';
      default: return '';
    }
  };

  // Validation functions
  const validatePersonalInfo = (): {[key: string]: string} => {
    const errors: {[key: string]: string} = {};
    
    if (!profileData.firstName.trim()) {
      errors.firstName = 'First name is required';
    }
    
    if (!profileData.lastName.trim()) {
      errors.lastName = 'Last name is required';
    }
    
    if (!profileData.phone.trim()) {
      errors.phone = 'Phone number is required';
    } else if (!/^[0-9+\-() ]{10,15}$/.test(profileData.phone.replace(/\s/g, ''))) {
      errors.phone = 'Please enter a valid phone number';
    }
    
    if (!profileData.dateOfBirth.trim()) {
      errors.dateOfBirth = 'Date of birth is required';
    }
    
    if (!profileData.availability.trim()) {
      errors.availability = 'Availability is required';
    }
    
    return errors;
  };

  const validateAddressInfo = (): {[key: string]: string} => {
    const errors: {[key: string]: string} = {};
    
    if (!profileData.addressLine1.trim()) {
      errors.addressLine1 = 'Address line 1 is required';
    }
    
    if (!profileData.selectedStateId) {
      errors.selectedStateId = 'State is required';
    }
    
    if (!profileData.selectedCityId) {
      errors.selectedCityId = 'City is required';
    }
    
    if (!profileData.pincode.trim()) {
      errors.pincode = 'Pincode is required';
    } else if (!/^[0-9]{6}$/.test(profileData.pincode)) {
      errors.pincode = 'Please enter a valid 6-digit pincode';
    }
    
    return errors;
  };

  const validateProfessionalInfo = (): {[key: string]: string} => {
    const errors: {[key: string]: string} = {};
    
    if (!profileData.experience.trim()) {
      errors.experience = 'Experience is required';
    } else if (parseInt(profileData.experience) < 0) {
      errors.experience = 'Experience must be a positive number';
    }
    
    if (!profileData.currentStatus.trim()) {
      errors.currentStatus = 'Current status is required';
    }
    
    if (!profileData.bio.trim()) {
      errors.bio = 'Bio/Description is required';
    }
    
    return errors;
  };

  const validateDocuments = (): {[key: string]: string} => {
    const errors: {[key: string]: string} = {};
    
    if (!profileData.documents.panCard) {
      errors.panCard = 'PAN Card is required';
    }
    
    if (!profileData.documents.aadharCard) {
      errors.aadharCard = 'Aadhar Card is required';
    }
    
    // Business License is optional - no validation required
    
    return errors;
  };

  const validateBankingInfo = (): {[key: string]: string} => {
    const errors: {[key: string]: string} = {};
    
    if (!profileData.bankDetails.accountHolderName.trim()) {
      errors['bankDetails.accountHolderName'] = 'Account holder name is required';
    }
    
    if (!profileData.bankDetails.accountNumber.trim()) {
      errors['bankDetails.accountNumber'] = 'Account number is required';
    } else if (!/^[0-9]{9,18}$/.test(profileData.bankDetails.accountNumber)) {
      errors['bankDetails.accountNumber'] = 'Please enter a valid account number (9-18 digits)';
    }
    
    if (!profileData.bankDetails.ifscCode.trim()) {
      errors['bankDetails.ifscCode'] = 'IFSC code is required';
    } else if (!/^[A-Z]{4}0[A-Z0-9]{6}$/.test(profileData.bankDetails.ifscCode)) {
      errors['bankDetails.ifscCode'] = 'Please enter a valid IFSC code';
    }
    
    if (!profileData.bankDetails.bankName.trim()) {
      errors['bankDetails.bankName'] = 'Bank name is required';
    }
    
    return errors;
  };

  const validateAllTabs = (): boolean => {
    const personalErrors = validatePersonalInfo();
    const addressErrors = validateAddressInfo();
    const professionalErrors = validateProfessionalInfo();
    const bankingErrors = validateBankingInfo();
    const documentErrors = validateDocuments();
    
    const allErrors = {
      ...personalErrors,
      ...addressErrors,
      ...professionalErrors,
      ...bankingErrors,
      ...documentErrors
    };
    
    setValidationErrors(allErrors);
    setShowValidation(true);
    
    return Object.keys(allErrors).length === 0;
  };

  const handleInputChange = (field: string, value: any) => {
    // Clear validation error for this field when user starts typing
    if (validationErrors[field] && showValidation) {
      setValidationErrors(prev => ({
        ...prev,
        [field]: ''
      }));
    }
    
    if (field.includes('.')) {
      const [parent, child] = field.split('.');
      setProfileData(prev => ({
        ...prev,
        [parent]: {
          ...(prev[parent as keyof ProviderProfileData] as any),
          [child]: value
        }
      }));
    } else {
      setProfileData(prev => ({
        ...prev,
        [field]: value
      }));
    }
  };

  const handleArrayChange = (field: string, value: string, checked: boolean) => {
    setProfileData(prev => ({
      ...prev,
      [field]: checked 
        ? [...(prev[field as keyof ProviderProfileData] as string[]), value]
        : (prev[field as keyof ProviderProfileData] as string[]).filter(item => item !== value)
    }));
  };

  const handleSave = async () => {
    setIsSaving(true);
    setError(null);
    
    // Validate all fields before saving and show validation errors
    setShowValidation(true);
    if (!validateAllTabs()) {
      setError('Please fill all the required fields before saving.');
      setIsSaving(false);
      return;
    }
    try {
      if (!user?.userUuid) {
        throw new Error('User information not available');
      }

      // Log current document state before creating payload
      console.log('📋 Current document state before save:', {
        panCard: profileData.documents.panCard,
        aadharCard: profileData.documents.aadharCard,
        businessLicense: profileData.documents.businessLicense
      });

      const apiPayload = {
        personalInfo: {
          firstName: profileData.firstName,
          lastName: profileData.lastName,
          phone: parseInt(profileData.phone) || 0, // Convert to number as per API
          dob: profileData.dateOfBirth
        },
        address: {
          id: null,
          addressUuid: null,
          addressType: "HOME",
          label: "Primary Address",
          line1: profileData.addressLine1,
          line2: profileData.addressLine2,
          cityId: profileData.selectedCityId || 0,    // Send cityId as per new API
          stateId: profileData.selectedStateId || 0,  // Send stateId as per new API
          postalCode: profileData.pincode,
          isDefault: true,
          createdAt: null
        },
        professionalInfo: {
          experienceYears: parseInt(profileData.experience) || 0, // Convert to number
          profileStatus: profileData.currentStatus,
          gstNo: profileData.gstNumber,
          bio: profileData.bio,
          availabilityType: profileData.availability
        },
        bankingDocuments: {
          accountHolderName: profileData.bankDetails.accountHolderName,
          accountNumber: profileData.bankDetails.accountNumber,
          ifscCode: profileData.bankDetails.ifscCode,
          bankName: profileData.bankDetails.bankName,
          panCard: profileData.documents.panCard ? {
            originalName: "",
            contentType: "",
            size: 0,
            downloadUrl: profileData.documents.panCard,
            exists: true
          } : {
            originalName: "",
            contentType: "",
            size: 0,
            downloadUrl: "",
            exists: false
          },
          aadharCard: profileData.documents.aadharCard ? {
            originalName: "",
            contentType: "",
            size: 0,
            downloadUrl: profileData.documents.aadharCard,
            exists: true
          } : {
            originalName: "",
            contentType: "",
            size: 0,
            downloadUrl: "",
            exists: false
          },
          miscDoc: profileData.documents.businessLicense && profileData.documents.businessLicense.trim() ? {
            originalName: "misc-document",
            contentType: "application/octet-stream", // Default content type
            size: 0,
            downloadUrl: profileData.documents.businessLicense.trim(),
            exists: true
          } : {
            originalName: "",
            contentType: "",
            size: 0,
            downloadUrl: "",
            exists: false
          }
        }
      };

      console.log('Saving profile data:', apiPayload);
      
      // Add specific logging for misc document inclusion
      console.log('🔍 Misc document in payload:', {
        exists: apiPayload.bankingDocuments.miscDoc.exists,
        downloadUrl: apiPayload.bankingDocuments.miscDoc.downloadUrl,
        fullMiscDoc: apiPayload.bankingDocuments.miscDoc
      });
      
      // Validation check for misc document
      if (profileData.documents.businessLicense && !apiPayload.bankingDocuments.miscDoc.exists) {
        console.warn('⚠️ Warning: Misc document URL exists in state but not marked as existing in payload!');
        console.warn('State value:', profileData.documents.businessLicense);
        console.warn('Payload exists flag:', apiPayload.bankingDocuments.miscDoc.exists);
      }
      
      console.log('📤 Sending profile data to API:', apiPayload);
      
      // Use authService.getApiInstance() to leverage token refresh interceptor
      const response = await authService.getApiInstance().put(
        `/api/provider/${user.userUuid}/profile`,
        apiPayload
      );

      const result = response.data;
      console.log('Profile saved successfully:', result);
      
      // Update local state with the saved data
      if (result) {
        mapApiDataToState(result);
        setIsNewProvider(false);
        setIsLegitimateNewProvider(false); // No longer a new provider after successful save
      }
      
      setSuccessMessage('Profile updated successfully!');
      setTimeout(() => setSuccessMessage(''), 3000);
    } catch (error) {
      console.error('Error saving profile:', error);
      const errorMessage = error instanceof Error ? error.message : 'Unknown error occurred';
      setSuccessMessage(`Error saving profile: ${errorMessage}`);
      setTimeout(() => setSuccessMessage(''), 5000);
    } finally {
      setIsSaving(false);
    }
  };

  // Document upload functions
  const uploadDocument = async (file: File, documentType: 'pan-card' | 'aadhar-card' | 'misc-document', setUploading: (loading: boolean) => void) => {
    if (!file || !user?.userUuid) return;

    // File validation
    const maxSize = 5 * 1024 * 1024; // 5MB
    const allowedTypes = ['image/jpeg', 'image/jpg', 'image/png', 'application/pdf'];

    if (file.size > maxSize) {
      setSuccessMessage('Error: File size should be less than 5MB');
      setTimeout(() => setSuccessMessage(''), 5000);
      return;
    }

    if (!allowedTypes.includes(file.type)) {
      setSuccessMessage('Error: Please upload only JPG, PNG, or PDF files');
      setTimeout(() => setSuccessMessage(''), 5000);
      return;
    }

    setUploading(true);
    try {
      const formData = new FormData();
      formData.append('file', file);

      // Use authService.getApiInstance() for file upload with token refresh support
      const response = await authService.getApiInstance().post(
        `/api/provider/${user.userUuid}/documents/${documentType}`,
        formData,
        {
          headers: {
            'Content-Type': 'multipart/form-data'
          }
        }
      );

      const result: DocumentUploadResponse = response.data;
      console.log('Document uploaded successfully:', result);

      // Update the corresponding document URL in the state
      if (documentType === 'pan-card') {
        console.log('🔵 Updating PAN card URL:', result.downloadUrl);
        handleInputChange('documents.panCard', result.downloadUrl);
      } else if (documentType === 'aadhar-card') {
        console.log('🔵 Updating Aadhar card URL:', result.downloadUrl);
        handleInputChange('documents.aadharCard', result.downloadUrl);
      } else if (documentType === 'misc-document') {
        console.log('🔵 Updating misc document URL:', result.downloadUrl);
        handleInputChange('documents.businessLicense', result.downloadUrl);
        
        // Also log the current state for debugging
        console.log('🔍 Misc document will be included in next profile save:', result.downloadUrl);
      }

      setSuccessMessage(`${result.message}`);
      setTimeout(() => setSuccessMessage(''), 3000);
    } catch (error) {
      console.error('Error uploading document:', error);
      const errorMessage = error instanceof Error ? error.message : 'Unknown error occurred';
      setSuccessMessage(`Error uploading document: ${errorMessage}`);
      setTimeout(() => setSuccessMessage(''), 5000);
    } finally {
      setUploading(false);
    }
  };

  const handlePanCardUpload = (file: File) => {
    uploadDocument(file, 'pan-card', setUploadingPanCard);
  };

  const handleAadharCardUpload = (file: File) => {
    uploadDocument(file, 'aadhar-card', setUploadingAadharCard);
  };

  const handleMiscDocUpload = (file: File) => {
    uploadDocument(file, 'misc-document', setUploadingMiscDoc);
  };

  const renderPersonalTab = () => (
    <div className="tab-content">
      <h3>Personal Information</h3>
      <div className="form-grid">
        <div className="form-group">
          <label>First Name *</label>
          <input
            type="text"
            value={profileData.firstName}
            onChange={(e) => {
              const value = e.target.value.replace(/[^a-zA-Z\s.''-]/g, '').slice(0, 50);
              handleInputChange('firstName', value);
            }}
            placeholder="Enter first name"
            maxLength={50}
            required
            className={validationErrors.firstName && showValidation ? 'error' : ''}
          />
          {validationErrors.firstName && showValidation && (
            <div className="validation-error">{validationErrors.firstName}</div>
          )}
          <small style={{ 
            display: 'block', 
            marginTop: '4px', 
            fontSize: '12px', 
            color: '#6c757d', 
            fontStyle: 'italic' 
          }}>
            Letters only, maximum 50 characters
          </small>
        </div>
        <div className="form-group">
          <label>Last Name *</label>
          <input
            type="text"
            value={profileData.lastName}
            onChange={(e) => {
              const value = e.target.value.replace(/[^a-zA-Z\s.''-]/g, '').slice(0, 50);
              handleInputChange('lastName', value);
            }}
            placeholder="Enter last name"
            maxLength={50}
            required
            className={validationErrors.lastName && showValidation ? 'error' : ''}
          />
          {validationErrors.lastName && showValidation && (
            <div className="validation-error">{validationErrors.lastName}</div>
          )}
          <small style={{ 
            display: 'block', 
            marginTop: '4px', 
            fontSize: '12px', 
            color: '#6c757d', 
            fontStyle: 'italic' 
          }}>
            Letters only, maximum 50 characters
          </small>
        </div>
        <div className="form-group">
          <label>Email *</label>
          <input
            type="email"
            value={profileData.email}
            onChange={(e) => handleInputChange('email', e.target.value)}
            required
          />
        </div>
        <div className="form-group">
          <label>Phone Number *</label>
          <input
            type="tel"
            value={profileData.phone}
            onChange={(e) => {
              const value = e.target.value.replace(/[^0-9+\-() ]/g, '').slice(0, 15);
              handleInputChange('phone', value);
            }}
            placeholder="Enter phone number"
            maxLength={15}
            inputMode="tel"
            required
            className={validationErrors.phone && showValidation ? 'error' : ''}
          />
          {validationErrors.phone && showValidation && (
            <div className="validation-error">{validationErrors.phone}</div>
          )}
          <small style={{ 
            display: 'block', 
            marginTop: '4px', 
            fontSize: '12px', 
            color: '#6c757d', 
            fontStyle: 'italic' 
          }}>
            Numbers and phone symbols only, maximum 15 characters
          </small>
        </div>
        <div className="form-group">
          <label>Date of Birth *</label>
          <input
            type="date"
            value={profileData.dateOfBirth}
            onChange={(e) => handleInputChange('dateOfBirth', e.target.value)}
            required
            className={validationErrors.dateOfBirth && showValidation ? 'error' : ''}
          />
          {validationErrors.dateOfBirth && showValidation && (
            <div className="validation-error">{validationErrors.dateOfBirth}</div>
          )}
        </div>
      </div>

      <h3>Address Information</h3>
      <div className="form-grid">
        <div className="form-group">
          <label>Address Line 1 *</label>
          <input
            type="text"
            value={profileData.addressLine1}
            onChange={(e) => {
              const value = e.target.value.slice(0, 100);
              handleInputChange('addressLine1', value);
            }}
            placeholder="House/Building number, Street name"
            maxLength={100}
            required
            className={validationErrors.addressLine1 && showValidation ? 'error' : ''}
          />
          {validationErrors.addressLine1 && showValidation && (
            <div className="validation-error">{validationErrors.addressLine1}</div>
          )}
          <small style={{ 
            display: 'block', 
            marginTop: '4px', 
            fontSize: '12px', 
            color: '#6c757d', 
            fontStyle: 'italic' 
          }}>
            Maximum 100 characters
          </small>
        </div>
        <div className="form-group">
          <label>Address Line 2</label>
          <input
            type="text"
            value={profileData.addressLine2}
            onChange={(e) => {
              const value = e.target.value.slice(0, 100);
              handleInputChange('addressLine2', value);
            }}
            placeholder="Area, Landmark (optional)"
            maxLength={100}
          />
          <small style={{ 
            display: 'block', 
            marginTop: '4px', 
            fontSize: '12px', 
            color: '#6c757d', 
            fontStyle: 'italic' 
          }}>
            Maximum 100 characters
          </small>
        </div>
        <div className="form-group">
          <label>State *</label>
          <select
            value={profileData.selectedStateId}
            onChange={(e) => {
              const stateId = parseInt(e.target.value);
              const selectedState = stateOptions.find(s => s.value === stateId);
              handleInputChange('selectedStateId', stateId);
              handleInputChange('state', selectedState?.label || '');
              // Reset city when state changes
              handleInputChange('selectedCityId', 0);
              handleInputChange('city', '');
            }}
            required
            className={validationErrors.selectedStateId && showValidation ? 'error' : ''}
          >
            <option value="0">Select State</option>
            {stateOptions.map(state => (
              <option key={state.value} value={state.value}>
                {state.label} ({state.code})
              </option>
            ))}
          </select>
          {validationErrors.selectedStateId && showValidation && (
            <div className="validation-error">{validationErrors.selectedStateId}</div>
          )}
          {isLoadingStates && <div className="loading-text">Loading states...</div>}
        </div>
        <div className="form-group">
          <label>City *</label>
          <select
            value={profileData.selectedCityId}
            onChange={(e) => {
              const cityId = parseInt(e.target.value);
              const selectedCity = cityOptions.find(c => c.value === cityId);
              handleInputChange('selectedCityId', cityId);
              handleInputChange('city', selectedCity?.label || '');
            }}
            required
            disabled={profileData.selectedStateId === 0}
            className={validationErrors.selectedCityId && showValidation ? 'error' : ''}
          >
            <option value="0">
              {profileData.selectedStateId === 0 ? 'Select State First' : 'Select City'}
            </option>
            {cityOptions.map(city => (
              <option key={city.value} value={city.value}>
                {city.label}
              </option>
            ))}
          </select>
          {validationErrors.selectedCityId && showValidation && (
            <div className="validation-error">{validationErrors.selectedCityId}</div>
          )}
          {isLoadingCities && <div className="loading-text">Loading cities...</div>}
        </div>
        <div className="form-group">
          <label>Pincode *</label>
          <input
            type="text"
            value={profileData.pincode}
            onChange={(e) => {
              const value = e.target.value.replace(/[^0-9]/g, '').slice(0, 6);
              handleInputChange('pincode', value);
            }}
            placeholder="Enter 6-digit pincode"
            maxLength={6}
            pattern="[0-9]{6}"
            inputMode="numeric"
            required
            className={validationErrors.pincode && showValidation ? 'error' : ''}
          />
          {validationErrors.pincode && showValidation && (
            <div className="validation-error">{validationErrors.pincode}</div>
          )}
        </div>
      </div>
      
      {/* Bottom Action Bar */}
      <div className="bottom-action-bar">
        <div className="bottom-nav-buttons">
          <button 
            className="prev-btn"
            disabled={true}
          >
            ← Previous
          </button>
          <button 
            className="next-btn"
            onClick={() => setActiveTab('professional')}
          >
            Next: Professional Info →
          </button>
        </div>
        <button 
          className="bottom-save-btn"
          onClick={handleSave}
          disabled={isSaving || loading}
        >
          {isSaving ? 'Saving...' : 'Save Changes'}
        </button>
      </div>
    </div>
  );

  const renderProfessionalTab = () => (
    <div className="tab-content">
      <h3>Professional Information</h3>
      <div className="form-grid">
        <div className="form-group">
          <label>Years of Experience *</label>
          <select
            value={profileData.experience}
            onChange={(e) => handleInputChange('experience', e.target.value)}
            required
            className={validationErrors.experience && showValidation ? 'error' : ''}
          >
            <option value="">Select Experience</option>
            <option value="1-2">1-2 years</option>
            <option value="3-5">3-5 years</option>
            <option value="6-10">6-10 years</option>
            <option value="11-15">11-15 years</option>
            <option value="15+">15+ years</option>
          </select>
          {validationErrors.experience && showValidation && (
            <div className="validation-error">{validationErrors.experience}</div>
          )}
        </div>
        <div className="form-group">
          <label>Choose what describes you the best *</label>
          <select
            value={profileData.currentStatus}
            onChange={(e) => handleInputChange('currentStatus', e.target.value)}
            required
            className={validationErrors.currentStatus && showValidation ? 'error' : ''}
          >
            <option value="">Select your current status</option>
            <option value="student">Student</option>
            <option value="research-scholar">Research Scholar</option>
            <option value="job-seeker">Job seeker</option>
            <option value="full-time-employed">Full time employed</option>
            <option value="part-time-employed">Part time employed</option>
            <option value="self-employed">Self employed</option>
          </select>
          {validationErrors.currentStatus && showValidation && (
            <div className="validation-error">{validationErrors.currentStatus}</div>
          )}
        </div>
        <div className="form-group">
          <label>GST Number (optional)</label>
          <input
            type="text"
            value={profileData.gstNumber}
            onChange={(e) => handleInputChange('gstNumber', e.target.value)}
            placeholder="Enter your GST number if applicable"
          />
        </div>
        <div className="form-group">
          <label>Availability *</label>
          <select
            value={profileData.availability}
            onChange={(e) => handleInputChange('availability', e.target.value)}
            required
            className={validationErrors.availability && showValidation ? 'error' : ''}
          >
            <option value="">Select Availability</option>
            <option value="WEEKDAYS">Weekdays Only</option>
            <option value="WEEKENDS">Weekends Only</option>
            <option value="ALL_DAYS">All Days</option>
            <option value="FLEXIBLE">Flexible</option>
          </select>
          {validationErrors.availability && showValidation && (
            <div className="validation-error">{validationErrors.availability}</div>
          )}
        </div>
      </div>

      <div className="form-group full-width">
        <label>Bio/Description *</label>
        <textarea
          value={profileData.bio}
          onChange={(e) => handleInputChange('bio', e.target.value)}
          rows={4}
          placeholder="Tell customers about your experience and expertise..."
          required
          className={validationErrors.bio && showValidation ? 'error' : ''}
        />
        {validationErrors.bio && showValidation && (
          <div className="validation-error">{validationErrors.bio}</div>
        )}
      </div>

      <h3>Specializations</h3>
      <div className="checkbox-grid">
        {specializationOptions.map(spec => (
          <label key={spec} className="checkbox-item">
            <input
              type="checkbox"
              checked={profileData.specializations.includes(spec)}
              onChange={(e) => handleArrayChange('specializations', spec, e.target.checked)}
            />
            <span>{spec}</span>
          </label>
        ))}
      </div>

      <h3>Languages</h3>
      <div className="checkbox-grid">
        {languageOptions.map(lang => (
          <label key={lang} className="checkbox-item">
            <input
              type="checkbox"
              checked={profileData.languages.includes(lang)}
              onChange={(e) => handleArrayChange('languages', lang, e.target.checked)}
            />
            <span>{lang}</span>
          </label>
        ))}
      </div>
      
      {/* Bottom Action Bar */}
      <div className="bottom-action-bar">
        <div className="bottom-nav-buttons">
          <button 
            className="prev-btn"
            onClick={() => setActiveTab('personal')}
          >
            ← Previous: Personal & Address
          </button>
          <button 
            className="next-btn"
            onClick={() => setActiveTab('banking')}
          >
            Next: Banking & Documents →
          </button>
        </div>
        <button 
          className="bottom-save-btn"
          onClick={handleSave}
          disabled={isSaving || loading}
        >
          {isSaving ? 'Saving...' : 'Save Changes'}
        </button>
      </div>
    </div>
  );

  const renderBankingTab = () => (
    <div className="tab-content">
      <h3>Bank Account Details</h3>
      <div className="form-grid">
        <div className="form-group">
          <label>Account Holder Name *</label>
          <input
            type="text"
            value={profileData.bankDetails.accountHolderName}
            onChange={(e) => handleInputChange('bankDetails.accountHolderName', e.target.value)}
            required
            className={validationErrors['bankDetails.accountHolderName'] && showValidation ? 'error' : ''}
          />
          {validationErrors['bankDetails.accountHolderName'] && showValidation && (
            <div className="validation-error">{validationErrors['bankDetails.accountHolderName']}</div>
          )}
        </div>
        <div className="form-group">
          <label>Account Number *</label>
          <input
            type="text"
            value={profileData.bankDetails.accountNumber}
            onChange={(e) => handleInputChange('bankDetails.accountNumber', e.target.value)}
            required
            className={validationErrors['bankDetails.accountNumber'] && showValidation ? 'error' : ''}
          />
          {validationErrors['bankDetails.accountNumber'] && showValidation && (
            <div className="validation-error">{validationErrors['bankDetails.accountNumber']}</div>
          )}
        </div>
        <div className="form-group">
          <label>IFSC Code *</label>
          <input
            type="text"
            value={profileData.bankDetails.ifscCode}
            onChange={(e) => handleInputChange('bankDetails.ifscCode', e.target.value.toUpperCase())}
            required
            className={validationErrors['bankDetails.ifscCode'] && showValidation ? 'error' : ''}
          />
          {validationErrors['bankDetails.ifscCode'] && showValidation && (
            <div className="validation-error">{validationErrors['bankDetails.ifscCode']}</div>
          )}
        </div>
        <div className="form-group">
          <label>Bank Name *</label>
          <input
            type="text"
            value={profileData.bankDetails.bankName}
            onChange={(e) => handleInputChange('bankDetails.bankName', e.target.value)}
            required
            className={validationErrors['bankDetails.bankName'] && showValidation ? 'error' : ''}
          />
          {validationErrors['bankDetails.bankName'] && showValidation && (
            <div className="validation-error">{validationErrors['bankDetails.bankName']}</div>
          )}
        </div>
      </div>

      <h3>Document Upload</h3>
      <div className="document-grid">
        {/* Aadhar Card Upload */}
        <div className="document-item">
          {/* <label>Aadhar Card *</label> */}
          <div className="document-content">
            <label>Aadhar Card *</label>
            {profileData.documents.aadharCard ? (
              <div className="existing-document">
                <a href={profileData.documents.aadharCard} target="_blank" rel="noopener noreferrer" className="document-link">
                  📄 View Current Document
                </a>
                <button 
                  type="button" 
                  className="replace-btn"
                  onClick={() => document.getElementById('aadhar-upload')?.click()}
                  disabled={uploadingAadharCard}
                >
                  {uploadingAadharCard ? 'Uploading...' : 'Replace'}
                </button>
              </div>
            ) : (
              <div className="upload-section">
                <button 
                  type="button" 
                  className={`upload-btn ${validationErrors.aadharCard && showValidation ? 'error' : ''}`}
                  onClick={() => document.getElementById('aadhar-upload')?.click()}
                  disabled={uploadingAadharCard}
                >
                  {uploadingAadharCard ? 'Uploading...' : 'Upload Aadhar Card'}
                </button>
              </div>
            )}
            <input 
              id="aadhar-upload"
              type="file" 
              accept="image/*,.pdf" 
              style={{ display: 'none' }}
              onChange={(e) => {
                const file = e.target.files?.[0];
                if (file) {
                  handleAadharCardUpload(file);
                }
              }}
            />
            <span className="file-info">Upload clear image or PDF</span>
            {validationErrors.aadharCard && showValidation && (
              <div className="validation-error">{validationErrors.aadharCard}</div>
            )}
          </div>
        </div>
        
        {/* PAN Card Upload */}
        <div className="document-item">
          {/* <label>PAN Card *</label> */}
          <div className="document-content">
            <label>PAN Card *</label>
            {profileData.documents.panCard ? (
              <div className="existing-document">
                <a href={profileData.documents.panCard} target="_blank" rel="noopener noreferrer" className="document-link">
                  📄 View Current Document
                </a>
                <button 
                  type="button" 
                  className="replace-btn"
                  onClick={() => document.getElementById('pan-upload')?.click()}
                  disabled={uploadingPanCard}
                >
                  {uploadingPanCard ? 'Uploading...' : 'Replace'}
                </button>
              </div>
            ) : (
              <div className="upload-section">
                <button 
                  type="button" 
                  className={`upload-btn ${validationErrors.panCard && showValidation ? 'error' : ''}`}
                  onClick={() => document.getElementById('pan-upload')?.click()}
                  disabled={uploadingPanCard}
                >
                  {uploadingPanCard ? 'Uploading...' : 'Upload PAN Card'}
                </button>
              </div>
            )}
            <input 
              id="pan-upload"
              type="file" 
              accept="image/*,.pdf" 
              style={{ display: 'none' }}
              onChange={(e) => {
                const file = e.target.files?.[0];
                if (file) {
                  handlePanCardUpload(file);
                }
              }}
            />
            <span className="file-info">Upload clear image or PDF</span>
            {validationErrors.panCard && showValidation && (
              <div className="validation-error">{validationErrors.panCard}</div>
            )}
          </div>
        </div>
        
        {/* Business License Upload */}
        <div className="document-item">
          {/* <label>Business License <span className="optional-text">(Optional)</span></label> */}
          <div className="document-content">
            <label>Other Documents <span className="optional-text">(Optional)</span></label>
            {profileData.documents.businessLicense ? (
              <div className="existing-document">
                <a href={profileData.documents.businessLicense} target="_blank" rel="noopener noreferrer" className="document-link">
                  📄 View Current Document
                </a>
                <button 
                  type="button" 
                  className="replace-btn"
                  onClick={() => document.getElementById('misc-upload')?.click()}
                  disabled={uploadingMiscDoc}
                >
                  {uploadingMiscDoc ? 'Uploading...' : 'Replace'}
                </button>
              </div>
            ) : (
              <div className="upload-section">
                <button 
                  type="button" 
                  className="upload-btn"
                  onClick={() => document.getElementById('misc-upload')?.click()}
                  disabled={uploadingMiscDoc}
                >
                  {uploadingMiscDoc ? 'Uploading...' : 'Upload Business License'}
                </button>
              </div>
            )}
            <input 
              id="misc-upload"
              type="file" 
              accept="image/*,.pdf" 
              style={{ display: 'none' }}
              onChange={(e) => {
                const file = e.target.files?.[0];
                if (file) {
                  handleMiscDocUpload(file);
                }
              }}
            />
            <span className="file-info">Upload clear image or PDF (if applicable)</span>
          </div>
        </div>
      </div>
      
      {/* Bottom Action Bar */}
      <div className="bottom-action-bar">
        <div className="bottom-nav-buttons">
          <button 
            className="prev-btn"
            onClick={() => setActiveTab('professional')}
          >
            ← Previous: Professional Info
          </button>
          <button 
            className="next-btn"
            disabled={true}
          >
            Next →
          </button>
        </div>
        <button 
          className="bottom-save-btn"
          onClick={handleSave}
          disabled={isSaving || loading}
        >
          {isSaving ? 'Saving...' : 'Save & Complete Profile'}
        </button>
      </div>
    </div>
  );

  return (
    <div className="provider-profile">
      {/* Header */}
      <div className="profile-header">
        <div className="header-content">
          <button className="back-btn" onClick={() => navigate(isProviderUnderReview() ? '/provider/under-review' : '/dashboard/provider')}>
            ← Back to {isProviderUnderReview() ? 'Review Status' : 'Dashboard'}
          </button>
          <div className="header-title">
            <h1>{isNewProvider ? 'Complete Your Profile' : 'Update Profile'}</h1>
            <p>{isNewProvider ? 'Welcome! Please complete your profile to start providing services' : 'Keep your profile information up to date'}</p>
          </div>
          <div className="header-actions">
            <button className="save-btn" onClick={handleSave} disabled={isSaving || loading}>
              {isSaving ? 'Saving...' : 'Save Changes'}
            </button>
          </div>
        </div>
        
        {/* Under Review Status Banner */}
        {isProviderUnderReview() && (
          <div className="under-review-banner">
            <div className="banner-content">
              <span className="banner-icon">⏳</span>
              <div className="banner-text">
                <strong>Account Under Review</strong>
                <p>Complete your profile and upload documents to speed up the approval process</p>
              </div>
              <div className="banner-actions">
                <button 
                  className="banner-btn"
                  onClick={() => navigate('/provider/under-review')}
                >
                  View Status
                </button>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Welcome Message for New Providers */}
      {isLegitimateNewProvider && !loading && (
        <div className="welcome-message">
          <h3>👋 Welcome to Antaryatra!</h3>
          <p>You're just a few steps away from offering your spiritual services. Please complete your profile information below to get started.</p>
          {showLoadProfileOption && (
            <div className="load-profile-option">
              <p><strong>Already have a profile?</strong></p>
              <button 
                className="load-profile-btn"
                onClick={loadProfileDataExplicitly}
                disabled={loading}
              >
                Load My Existing Profile
              </button>
              <small>Click this if you've already created a profile before</small>
            </div>
          )}
        </div>
      )}

      {/* Success Message */}
      {successMessage && (
        <div className="success-message">
          ✓ {successMessage}
        </div>
      )}

      {/* Error Message */}
      {error && (
        <div className="error-message">
          ⚠️ {error}
          <button onClick={() => setError(null)} className="error-close">×</button>
        </div>
      )}

      {/* Loading State */}
      {loading ? (
        <div className="loading-container">
          <div className="loading-spinner"></div>
          <p>Loading profile data...</p>
        </div>
      ) : (
        <div className="profile-content">
          <div className="tab-navigation">
            <button 
              className={`tab-btn ${activeTab === 'personal' ? 'active' : ''}`}
              onClick={() => setActiveTab('personal')}
            >
              Personal & Address
            </button>
            <button 
              className={`tab-btn ${activeTab === 'professional' ? 'active' : ''}`}
              onClick={() => setActiveTab('professional')}
            >
              Professional Info
            </button>
            <button 
              className={`tab-btn ${activeTab === 'banking' ? 'active' : ''}`}
              onClick={() => setActiveTab('banking')}
            >
              Banking & Documents
            </button>
          </div>

          <div className="tab-container">
            {activeTab === 'personal' && renderPersonalTab()}
            {activeTab === 'professional' && renderProfessionalTab()}
            {activeTab === 'banking' && renderBankingTab()}
          </div>
        </div>
      )}
    </div>
  );
};

export default ProviderProfile;